/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext } from 'react';

import { ContentContext } from 'context/ContentContext';

import { AccessRoutes } from 'ui/organisms/AccessRoutes/AccessRoutes';
import type { AccessRoute } from 'ui/organisms/AccessRoutes/AccessRoutes.types';
import { NumberedFeatureCards } from 'ui/organisms/NumberedFeatureCards/NumberedFeatureCards';

import { McpHeroSection } from 'features/mcp/organisms/McpHeroSection/McpHeroSection';
import { McpClientsSection } from 'features/mcp/organisms/McpClientsSection/McpClientsSection';
import { McpToolsCatalogSection } from 'features/mcp/organisms/McpToolsCatalogSection/McpToolsCatalogSection';
import { McpExamplePromptsSection } from 'features/mcp/organisms/McpExamplePromptsSection/McpExamplePromptsSection';
import { McpFinalCtaSection } from 'features/mcp/organisms/McpFinalCtaSection/McpFinalCtaSection';

import styles from './McpIntegrationPage.module.scss';

const MCP_PROBLEM_ITEMS = [
  {
    num: '01',
    tag: 'SOURCE',
    title: 'From the registry',
    description:
      'Every record traces back to an official government source, with attribution attached. When compliance asks where a number came from, you have an answer.',
  },
  {
    num: '02',
    tag: 'LIVE',
    title: 'Queried in real time',
    description:
      'Your agent calls the data the moment it needs it. No exports, no overnight syncs, no model hallucinating a director’s name.',
  },
  {
    num: '03',
    tag: 'STRUCTURED',
    title: 'One schema, 200+ countries',
    description:
      'A Delaware C-corp and a Moldovan SRL come back in the same shape. Your AI reasons over consistent, typed fields.',
  },
];

const MCP_FIRST_PARTY_ITEMS = [
  {
    num: '01',
    tag: 'AUDIT-READY',
    title: 'Every field is defensible',
    description:
      'Each record points back to an official filing with a verification timestamp. Built for teams who get audited on their sources.',
  },
  {
    num: '02',
    tag: 'GLOBAL',
    title: "Coverage you can't replicate",
    description: "One integration replaces 400 separate registry connections you'd otherwise build and maintain.",
  },
  {
    num: '03',
    tag: 'ENTERPRISE',
    title: 'Trusted at scale',
    description: "The same data powering LSEG, Mastercard, Experian, SAP, PwC and Kroll — now in your model's context.",
  },
];

const MCP_SETUP_STEPS: AccessRoute[] = [
  {
    name: 'Get your key',
    desc: 'Click Start for Free and generate an API key from the dashboard. No card.',
  },
  {
    name: 'Connect the server',
    desc: 'Paste one line into your MCP client config and authenticate with your key.',
  },
  {
    name: 'Ask',
    desc: 'Your assistant now queries 600M companies from live registry data — in plain language.',
  },
];

const McpIntegrationPage = () => {
  const { secondaryNavigation } = useContext(ContentContext);

  const setupSteps: AccessRoute[] = MCP_SETUP_STEPS.map((step, index) =>
    index === MCP_SETUP_STEPS.length - 1
      ? { ...step, actions: [{ label: 'Request a demo', route: secondaryNavigation.book_demo.slug }] }
      : step,
  );

  return (
    <div className={styles.wrapper}>
      <McpHeroSection />
      <McpClientsSection />

      <NumberedFeatureCards
        title="A model is only as good as the data it can reach."
        description="Most company data an LLM sees is stale, second-hand, or invented on the spot. Connect ours and your AI answers from the registry itself."
        columns={3}
        items={MCP_PROBLEM_ITEMS}
      />

      <McpToolsCatalogSection />

      <AccessRoutes
        kicker="60-Second Setup"
        heading="Three steps. Then ask your AI anything."
        lede="Connect in under 60 seconds."
        numbered
        background="navy"
        routes={setupSteps}
      />

      <McpExamplePromptsSection />

      <NumberedFeatureCards
        theme="light"
        title="The difference is where the data starts."
        description="We have spent ten years wiring directly into government registries. That is the moat — and now it's a single MCP endpoint."
        columns={3}
        items={MCP_FIRST_PARTY_ITEMS}
      />

      <McpFinalCtaSection />
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default McpIntegrationPage;
